import { useEffect, useState } from 'react';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import TileCard from '../components/TileCard';

/**
 * Leaderboard mini tile. Pulls the current round for a named group via
 * /api/leaderboard/:groupName, which returns:
 *   { group, kpis: [{ id, name, grand_total, rankings, display_format, ... }] }
 *
 * Rendering modes (controlled by `mode` prop):
 *   'team_totals' (default) — one row per KPI, showing the team-wide
 *                             sum ('New Accounts: 42', 'Number of Tows:
 *                             318'). Matches Matt's spec: "Do totals
 *                             for the whole team on these."
 *   'rankings'              — top-N ranked users across the group
 *                             (useful for driver board views).
 *
 * Optional footer (showFooterTotals) adds a gold-underlined "This
 * Week / This Month" block pulled from /api/dashboard/tow-totals.
 * Used by CEO Driver Leaderboard.
 *
 * Props:
 *   groupName          (optional if groupNames provided — required otherwise)
 *   groupNames         (optional — array of group slugs to aggregate across;
 *                       useful for rolling up sub-teams into a company-wide
 *                       view. KPIs with the same name across groups are
 *                       summed; rankings are concatenated then re-ranked
 *                       by total.)
 *   title              (optional — defaults to groupName)
 *   kpiNames           (optional — filter to specific KPI names)
 *   mode               (optional — 'team_totals' (default) | 'rankings')
 *   showFooterTotals   (optional)
 *   limit              (optional, default 5)
 */
export default function LeaderboardMiniTile({
  groupName: groupNameProp,
  groupNames: groupNamesProp,
  title,
  kpiNames,
  mode = 'team_totals',
  showFooterTotals = false,
  footerTowGroups,    // e.g. ['driver','dallas','denton','fort_worth']
                       // — sums their leaderboard entries for the footer
  limit = 5,
}) {
  const { subTeamSlug } = useAuth();

  // Special sentinel: groupName='$sub_team' resolves at render time to the
  // user's sub-team slug (e.g. Dallas driver → 'dallas'). Falls back to
  // 'driver' if the user isn't on a sub-team (top-level Driver Operations).
  const groupName = groupNameProp === '$sub_team'
    ? (subTeamSlug || 'driver')
    : groupNameProp;

  // Multi-group mode: fetch each group's leaderboard in parallel, merge
  // KPIs by name (sum grand_total + concat rankings), then re-rank. Falls
  // back to single-group mode when groupNames is absent/empty.
  const groupNames = Array.isArray(groupNamesProp) && groupNamesProp.length > 0
    ? groupNamesProp
    : null;
  const [state, setState] = useState({ loading: true, error: null, data: null, totals: null });

  useEffect(() => {
    let cancelled = false;
    const mainTask = groupNames
      ? Promise.all(groupNames.map(gn => api.getLeaderboard(gn).catch(() => null)))
          .then(results => mergeLeaderboards(results.filter(Boolean), groupNames.join('+')))
          .catch(err => ({ __error: err?.message || 'failed' }))
      : api.getLeaderboard(groupName).catch(err => ({ __error: err?.message || 'failed' }));

    const tasks = [mainTask];
    if (showFooterTotals) {
      // Pass through the explicit list of driver-ish groups when provided,
      // so the footer stays in lockstep with the leaderboards it sums.
      tasks.push(api.getDashboardTowTotals(footerTowGroups).catch(() => null));
    }

    Promise.all(tasks).then(([data, totals]) => {
      if (cancelled) return;
      if (data && data.__error) {
        setState({ loading: false, error: data.__error, data: null, totals: null });
        return;
      }
      setState({ loading: false, error: null, data, totals: totals || null });
    });
    return () => { cancelled = true; };
    // footerTowGroups is an array — join to a stable string for the dep list
  }, [groupName, (groupNames || []).join(','), showFooterTotals, (footerTowGroups || []).join(',')]);

  const kpis = Array.isArray(state.data?.kpis) ? state.data.kpis : [];
  const filteredKpis = kpiNames && kpiNames.length > 0
    ? kpis.filter(k => kpiNames.includes(k.name))
    : kpis;

  // Empty state logic — if the group has KPIs configured but none have
  // any data yet (all grand_totals are 0 AND all rankings are empty),
  // we still show the tile (empty totals look informative). Only the
  // "no KPIs at all" case triggers the empty-label.
  const isEmpty = !state.loading && !state.error && kpis.length === 0;

  return (
    <TileCard
      title={title || groupName}
      loading={state.loading}
      error={state.error}
      empty={isEmpty}
      emptyLabel="No leaderboard data yet"
      actionHref="/leaderboard"
      actionLabel="Full board →"
    >
      {mode === 'team_totals' ? (
        <TeamTotalsView kpis={filteredKpis} limit={limit} />
      ) : (
        <RankingsView kpis={filteredKpis} limit={limit} />
      )}

      {showFooterTotals && state.totals && (
        <div className="dash-lb-footer">
          <div className="dash-lb-footer-item">
            <div className="dash-lb-footer-value">{state.totals.week_total ?? 0}</div>
            <div className="dash-lb-footer-label">This Week</div>
          </div>
          <div className="dash-lb-footer-item">
            {/* pay_period_total = last 2 rounds summed (biweekly payroll).
                Falls back to month_total for back-compat if server hasn't
                redeployed yet — avoids a blank stat during the window
                between client deploy and server deploy. */}
            <div className="dash-lb-footer-value">
              {state.totals.pay_period_total ?? state.totals.month_total ?? 0}
            </div>
            <div className="dash-lb-footer-label">This Pay Period</div>
          </div>
        </div>
      )}
    </TileCard>
  );
}

/**
 * Team totals view — one row per KPI with the team-wide value for the
 * CURRENT (latest) round only, not the all-time cumulative grand_total.
 *
 * Why: the dashboard shows "this week's" snapshot. A KPI called
 * "Day Drivers Weekly Tow Counts" that's been running for 5 weeks has
 * grand_total=185 but current-round-only might be 19. Users reading
 * the tile expect the former to reflect this week, not the running
 * total. Falls back to grand_total if the KPI has no rounds yet.
 */
function TeamTotalsView({ kpis, limit }) {
  if (kpis.length === 0) {
    return <div className="dash-card-empty" style={{ flex: 'initial', padding: '20px 0' }}>No KPIs configured</div>;
  }
  return (
    <div className="dash-list">
      {kpis.slice(0, limit).map(k => {
        const value = currentRoundTeamValue(k);
        return (
          <div key={k.id} className="dash-lb-row">
            <span className="dash-lb-name">{k.name}</span>
            <span className="dash-lb-value">{formatValue(value, k.display_format, k.metric_type)}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Sum the team's values for the CURRENT round of a KPI = the newest round
 * that has any numbers (same rule as LeaderboardPage and /tow-totals).
 * Server ships rounds ordered by display_order + id (oldest first, newest
 * last), and each ranking row has round_values: { round_id: value }.
 * Taking the plain last round read 0 all through Week 1 of a pay period,
 * because Week 2 sits empty until it starts. No rounds → grand_total.
 */
function currentRoundTeamValue(k) {
  const rounds = Array.isArray(k.rounds) ? k.rounds : [];
  if (rounds.length === 0) return k.grand_total ?? 0;
  const rankings = Array.isArray(k.rankings) ? k.rankings : [];
  const hasData = rid => rankings.some(r => r.round_values?.[rid] != null);
  const latest = [...rounds].reverse().find(r => hasData(r.id)) || rounds[rounds.length - 1];
  const latestRoundId = latest.id;
  let total = 0;
  for (const r of rankings) {
    const rv = r.round_values?.[latestRoundId];
    if (rv != null) total += Number(rv) || 0;
  }
  return total;
}

/**
 * Rankings view — top-N users across all KPIs in the group. Walks each
 * KPI's rankings array and takes the top ranked user per KPI. For a
 * single-KPI group this is the classic leaderboard; for multi-KPI we
 * surface the #1 per KPI so the tile stays compact.
 */
function RankingsView({ kpis, limit }) {
  // Flatten: collect all (kpi_name, ranking_row) pairs
  const rows = [];
  for (const k of kpis) {
    const rank = Array.isArray(k.rankings) ? k.rankings.filter(r => r.has_data) : [];
    for (const r of rank.slice(0, limit)) {
      rows.push({
        kpiName: k.name,
        displayFormat: k.display_format,
        metricType: k.metric_type,
        ...r,
      });
    }
  }
  if (rows.length === 0) {
    return <div className="dash-card-empty" style={{ flex: 'initial', padding: '20px 0' }}>No entries yet</div>;
  }
  return (
    <div className="dash-list">
      {rows.slice(0, limit).map((r, i) => (
        <div key={`${r.kpiName}-${r.user_id}-${i}`} className="dash-lb-row">
          <span className="dash-lb-name">
            {r.rank ? `${r.rank}. ` : ''}{r.full_name || `User #${r.user_id}`}
            <div className="dash-list-sub" style={{ marginTop: 2 }}>{r.kpiName}</div>
          </span>
          <span className="dash-lb-value">{formatValue(r.total, r.displayFormat, r.metricType)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Merge multiple /api/leaderboard/:group responses into one combined view.
 *
 * Why: when CEO dashboard wants a "company-wide driver board" but every
 * driver is actually enrolled in a sub-team leaderboard (dallas / denton
 * / fort_worth / day) — there is no single group that knows all the
 * drivers. Instead of duplicating memberships in a parent 'driver' group,
 * fetch each sub-team and stitch them together client-side.
 *
 * Merge semantics:
 *   - KPIs with the SAME NAME across groups are folded into one entry:
 *     grand_total summed, rankings concatenated, then re-ranked by total.
 *     Rounds from the first group win (identical config by construction).
 *   - Unique KPI names from any group are kept as-is.
 *   - User collisions (same user appearing in multiple groups for the
 *     same KPI) are summed into one row using user_id as the key.
 */
function mergeLeaderboards(responses, label) {
  if (responses.length === 0) return { group: label, kpis: [] };
  if (responses.length === 1) return responses[0];

  // kpiName → { base KPI row, userMap: { user_id: { full_name, photo_url, total, round_values } } }
  const kpiMap = new Map();

  for (const res of responses) {
    const kpis = Array.isArray(res?.kpis) ? res.kpis : [];
    for (const k of kpis) {
      const key = k.name;
      if (!kpiMap.has(key)) {
        kpiMap.set(key, {
          base: { ...k, grand_total: 0, rankings: [], rounds: k.rounds || [] },
          userMap: new Map(),
        });
      }
      const slot = kpiMap.get(key);
      slot.base.grand_total = (slot.base.grand_total || 0) + (k.grand_total || 0);

      const rankings = Array.isArray(k.rankings) ? k.rankings : [];
      for (const r of rankings) {
        const prev = slot.userMap.get(r.user_id);
        if (!prev) {
          slot.userMap.set(r.user_id, {
            user_id: r.user_id,
            full_name: r.full_name,
            photo_url: r.photo_url || null,
            total: r.total || 0,
            has_data: !!r.has_data,
            round_values: { ...(r.round_values || {}) },
          });
        } else {
          prev.total = (prev.total || 0) + (r.total || 0);
          prev.has_data = prev.has_data || !!r.has_data;
          // Merge round values (sum overlapping round_ids)
          for (const [rid, rv] of Object.entries(r.round_values || {})) {
            prev.round_values[rid] = (prev.round_values[rid] || 0) + (Number(rv) || 0);
          }
        }
      }
    }
  }

  // Flatten + re-rank per KPI
  const direction = (k) => (k.value_direction === 'lower_is_better' ? 1 : -1);
  const mergedKpis = Array.from(kpiMap.values()).map(({ base, userMap }) => {
    const users = Array.from(userMap.values())
      .sort((a, b) => direction(base) * ((a.total || 0) - (b.total || 0)));
    let rank = 0;
    let lastTotal = null;
    const rankings = users.map((u, i) => {
      if (u.has_data && u.total !== lastTotal) { rank = i + 1; lastTotal = u.total; }
      return { ...u, rank: u.has_data ? rank : null };
    });
    return { ...base, rankings };
  });

  return { group: label, kpis: mergedKpis };
}

function formatValue(v, format, metricType) {
  if (v == null) return '—';
  const type = format || metricType;
  if (type === 'currency') return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  if (type === 'percentage') return `${Number(v).toFixed(1)}%`;
  return Number(v).toLocaleString();
}
